import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { getIcon } from "../lib/icons";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { toast } from "sonner";
import { Star, CheckCircle2, PawPrint } from "lucide-react";
import { setMeta } from "../lib/seo";

function ReviewItemCard({ item, displayName, token, onDone }) {
  const { t } = useLang();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(item.already_reviewed);

  const Icon = getIcon(item.icon);
  const imgSrc = item.image_url ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        review_token: token,
        product_id: item.product_id,
        display_name: displayName.trim() || "Pet Munchies Customer",
        rating,
        review_text: text.trim(),
      });
      setDone(true);
      onDone(item.product_id);
      toast.success(t("reviews.thanks"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="neo-card p-4 flex flex-col sm:flex-row gap-4" data-testid={`review-item-${item.product_id}`}>
      <div
        className="w-20 h-20 rounded-xl flex items-center justify-center overflow-hidden shrink-0"
        style={{ background: item.color, border: "2px solid #0F172A" }}
      >
        {imgSrc ? (
          <img src={imgSrc} alt={item.name} className="w-full h-full object-contain p-2" />
        ) : (
          <Icon size={36} className="text-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        {item.slug ? (
          <Link to={`/products/${item.slug}`} className="font-display font-semibold hover:underline">
            {item.name}
          </Link>
        ) : (
          <div className="font-display font-semibold">{item.name}</div>
        )}

        {done ? (
          <p
            className="text-sm font-semibold mt-2 inline-flex items-center gap-1"
            style={{ color: "#059669" }}
            data-testid={`review-item-done-${item.product_id}`}
          >
            <CheckCircle2 size={16} /> {item.already_reviewed ? t("reviews.already_reviewed") : t("reviews.thanks")}
          </p>
        ) : (
          <form onSubmit={submit} className="mt-2 flex flex-col gap-2" data-testid={`review-item-form-${item.product_id}`}>
            <div className="inline-flex items-center gap-1" data-testid={`review-item-stars-${item.product_id}`}>
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setRating(i)}
                  data-testid={`review-item-star-${item.product_id}-${i}`}
                  aria-label={`${i} star`}
                >
                  <Star size={20} className={i <= rating ? "text-yellow-400" : "text-slate-300"} fill={i <= rating ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
            <Textarea
              required
              rows={2}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("reviews.your_review")}
              data-testid={`review-item-text-${item.product_id}`}
            />
            <button
              type="submit"
              disabled={submitting}
              className="neo-btn self-start px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
              style={{ background: "var(--pm-dog)" }}
              data-testid={`review-item-submit-${item.product_id}`}
            >
              {submitting ? t("reviews.submitting") : t("reviews.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function OrderReviewPage() {
  const { token } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setMeta("robots", "noindex, nofollow");
    return () => setMeta("robots", "index, follow");
  }, []);

  useEffect(() => {
    api
      .get(`/orders/review/${token}`)
      .then((r) => {
        setData(r.data);
        setDisplayName(r.data.full_name || "");
      })
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [token]);

  const markDone = (productId) => {
    setData((prev) => ({
      ...prev,
      items: prev.items.map((it) => (it.product_id === productId ? { ...it, already_reviewed: true } : it)),
    }));
  };

  return (
    <div data-testid="order-review-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 min-h-[50vh]">
        {loading ? (
          <p className="text-center text-slate-500 py-16">Loading…</p>
        ) : invalid || !data ? (
          <div className="text-center py-16" data-testid="review-link-invalid">
            <PawPrint size={48} className="mx-auto text-slate-300" />
            <h1 className="font-display text-2xl font-bold mt-4">{t("reviews.invalid_title")}</h1>
            <p className="text-slate-500 mt-2">{t("reviews.invalid_desc")}</p>
            <Link to="/" className="neo-btn inline-flex items-center gap-2 px-4 py-2 mt-6 bg-white">
              {t("pdp.back")}
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold" data-testid="review-page-title">
              {t("reviews.rate_order_title")}
            </h1>
            <p className="text-slate-600 mt-1" data-testid="review-page-desc">
              {t("reviews.rate_order_desc", { name: (data.full_name || "").split(" ")[0] })} ({data.order_number})
            </p>

            <label className="flex flex-col gap-1 mt-5 max-w-sm">
              <span className="text-sm font-semibold">{t("reviews.your_name")}</span>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="review-display-name-input" />
            </label>

            <div className="mt-6 flex flex-col gap-4">
              {data.items.map((item) => (
                <ReviewItemCard key={item.product_id} item={item} displayName={displayName} token={token} onDone={markDone} />
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
